var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useEffect, useMemo, useRef } from 'react';
import throttle from 'lodash/throttle';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { CACHE_KEY, DEVICE_LEVEL, DEVICE_TYPE, POPUP_TYPE_TEXTS } from '@tving/utils/src/constants/commonConstants';
import { TICKET_TIER } from '@tving/utils/src/constants/userConstants';
import useStateRef from '@tving/utils/src/hooks/common/useStateRef';
import getDeviceType from '@tving/utils/src/utils/common/getDeviceType/getDeviceType';
import isBoard from '@tving/utils/src/utils/common/isBoard/isBoard';
import DeviceManagerInstance from '@tving/utils/src/utils/device';
import { KEY_CODE } from '@tving/utils/src/utils/tving-focusable/contants';
import { PageNavigator } from '@/index';
import { useCursorStore, useDeviceStore, usePlayerStore, usePopup } from '@/stores';
import useAccount from '../../hooks/account/useAccount';
import useEntry from '../../hooks/entry/useEntry';
/**
 * 티비 앱 진입 시 필요한 데이터를 로드한 뒤 스플래시를 재생하게 합니다..
 */
var EntryLayer = function (_a) {
    var setIsLoaded = _a.setIsLoaded;
    var wheelValue = useRef(0);
    var location = useLocation();
    var _b = __read(useCursorStore(function (state) { return [state.isCursorVisible, state.setCursorVisible]; }, shallow), 2), isCursorVisible = _b[0], setCursorVisible = _b[1];
    var showPopup = usePopup(function (state) { return state.showPopup; });
    var _c = __read(useCookies([CACHE_KEY.USER_TOKEN, CACHE_KEY.AUTH_TOKEN, CACHE_KEY.ACCESS_TOKEN, CACHE_KEY.REFRESH_TOKEN]), 1), cookies = _c[0];
    var legacyUserInfoString = localStorage.getItem(CACHE_KEY.LEGACY_USER_INFO);
    var legacyUserInfo = legacyUserInfoString ? JSON.parse(legacyUserInfoString) : null;
    var userToken = localStorage.getItem(CACHE_KEY.USER_TOKEN) || (legacyUserInfo === null || legacyUserInfo === void 0 ? void 0 : legacyUserInfo.tt) || cookies[CACHE_KEY.USER_TOKEN] || undefined;
    var lastPasswordUpdateDate = localStorage.getItem(CACHE_KEY.LAST_PASSWORD_UPDATE_DATE) || cookies[CACHE_KEY.LAST_PASSWORD_UPDATE_DATE] || undefined;
    var authTokenRef = useStateRef(localStorage.getItem(CACHE_KEY.AUTH_TOKEN) || cookies[CACHE_KEY.AUTH_TOKEN] || undefined);
    var accessTokenRef = useStateRef(localStorage.getItem(CACHE_KEY.ACCESS_TOKEN) || cookies[CACHE_KEY.ACCESS_TOKEN] || undefined);
    var profileNo = localStorage.getItem(CACHE_KEY.PROFILE_NO) || cookies[CACHE_KEY.PROFILE_NO] || undefined;
    var entryData = useEntry(Math.floor(Date.now() / (1000 * 60))).data;
    var doAutoLogin = useAccount().doAutoLogin;
    var tvingPlayer = usePlayerStore(function (state) { return state.tvingPlayer; });
    var isLoadedRef = useRef(false);
    var _d = __read(useDeviceStore(function (state) { return [state.setDeviceLevel, state.setDeviceInfo]; }, shallow), 2), setDeviceLevel = _d[0], setDeviceInfo = _d[1];
    var setDeviceLevelRef = useStateRef(setDeviceLevel);
    var setDeviceInfoRef = useStateRef(setDeviceInfo);
    useEffect(function () {
        if (isLoadedRef.current) {
            return;
        }
        var setDeviceLevel = setDeviceLevelRef.current;
        var setDeviceInfo = setDeviceInfoRef.current;
        var checkDeviceLevel = function (retry) {
            if (retry === void 0) { retry = 0; }
            var deviceType = getDeviceType();
            var deviceInfo = DeviceManagerInstance.getDeviceInfo();
            var osVersion = deviceInfo.osVersion;
            if (!osVersion) {
                if (retry < 3) {
                    setTimeout(function () {
                        checkDeviceLevel(retry + 1);
                    }, 1000);
                }
            }
            else {
                var deviceLevel = DEVICE_LEVEL.NORMAL;
                var deviceModel = deviceInfo.deviceModel;
                var upperCaseDeviceModel = deviceModel ? deviceModel.toUpperCase() : '';
                if ((deviceType === DEVICE_TYPE.WEBOS && Number(osVersion.split('.')[0]) <= 3) ||
                    (deviceType === DEVICE_TYPE.TIZEN && Number(osVersion.split('.')[0]) <= 2) ||
                    upperCaseDeviceModel === 'S60UPI') {
                    deviceLevel = DEVICE_LEVEL.LOW;
                }
                setDeviceLevel(deviceLevel);
                if (entryData === null || entryData === void 0 ? void 0 : entryData.lastUpdateDate) {
                    deviceInfo.lastUpdateDate = entryData.lastUpdateDate;
                }
                setDeviceInfo(deviceInfo);
            }
        };
        checkDeviceLevel();
    }, [setDeviceLevelRef, setDeviceInfoRef, entryData]);
    useEffect(function () {
        var handleCursorStateChange = function (event) {
            var detail = event.detail;
            setCursorVisible(detail.visibility);
        };
        document.addEventListener('cursorStateChange', handleCursorStateChange);
        return function () {
            document.removeEventListener('cursorStateChange', handleCursorStateChange);
        };
    }, [setCursorVisible]);
    useEffect(function () {
        var handleMouseOver = function (event) {
            if (!isCursorVisible) {
                event.stopPropagation();
                event.preventDefault();
                return false;
            }
            return true;
        };
        document.addEventListener('mouseover', handleMouseOver);
        return function () {
            document.removeEventListener('mouseover', handleMouseOver);
        };
    }, [isCursorVisible]);
    /**
     * 앱 업데이트 시 업데이트 가능한 페이지 여부 검사
     */
    var isValidReloadRoute = useMemo(function () {
        var route = location === null || location === void 0 ? void 0 : location.pathname.split('/')[1].toUpperCase();
        return isBoard(route);
    }, [location === null || location === void 0 ? void 0 : location.pathname]);
    var tvingPlayerRef = useStateRef(tvingPlayer);
    useEffect(function () {
        var tvingPlayer = tvingPlayerRef.current;
        if (entryData) {
            if (entryData.redirectUrl) {
                var redirectUrl = entryData.redirectUrl, isHref = entryData.isHref;
                if (isHref) {
                    // 버퍼 체크 전에는 버퍼 페이지로 이동
                    if (tvingPlayer && tvingPlayer.stop) {
                        tvingPlayer.stop();
                    }
                    var bufferSessionStorageValue = {
                        isCheckedYn: 'Y',
                        timestamp: Date.now(),
                    };
                    sessionStorage.setItem(CACHE_KEY.BUFFER, JSON.stringify(bufferSessionStorageValue));
                    window.location.href = redirectUrl;
                }
                else {
                    PageNavigator.replace(redirectUrl);
                }
            }
        }
    }, [entryData, tvingPlayerRef]);
    useEffect(function () {
        if (entryData) {
            if (entryData.isUpdated && isValidReloadRoute) {
                showPopup(POPUP_TYPE_TEXTS.UPDATE_VERSION, [
                    {
                        text: '확인',
                        handleSelect: function () {
                            window.location.href = window.location.origin;
                        },
                    },
                ], function () {
                    window.location.href = window.location.origin;
                });
            }
        }
    }, [entryData, isValidReloadRoute, showPopup]);
    // eslint-disable-next-line consistent-return
    useEffect(function () {
        var deviceType = getDeviceType();
        var handleOnWheel = function (event) {
            var deltaY = event.deltaY;
            wheelValue.current += deltaY;
            if (Math.abs(wheelValue.current) > 50) {
                var keyDownEvent = new KeyboardEvent('keydown', {
                    bubbles: true,
                    cancelable: true,
                });
                var keyCode_1 = KEY_CODE.UP;
                if (wheelValue.current > 0) {
                    keyCode_1 = KEY_CODE.DOWN;
                }
                Object.defineProperty(keyDownEvent, 'keyCode', {
                    get: function () { return keyCode_1; },
                });
                document.dispatchEvent(keyDownEvent);
            }
            var wheelTimer = setTimeout(function () {
                clearTimeout(wheelTimer);
                wheelValue.current = 0;
            }, 250);
        };
        if (deviceType === DEVICE_TYPE.WEB || deviceType === DEVICE_TYPE.WEBOS) {
            var throttledHandleOnWheel_1 = throttle(handleOnWheel, 200);
            document.addEventListener('wheel', throttledHandleOnWheel_1);
            return function () {
                document.removeEventListener('wheel', throttledHandleOnWheel_1);
            };
        }
    }, []);
    var locationPathRef = useStateRef(location.pathname);
    var doAutoLoginRef = useStateRef(doAutoLogin);
    // 로딩 완료 후 자동 로그인 및 스플래시 애니메이션 재생 처리
    useEffect(function () {
        if (entryData && !entryData.isSkipSplash && !isLoadedRef.current) {
            isLoadedRef.current = true;
            var authToken = authTokenRef.current;
            if (userToken) {
                var doAutoLogin_1 = doAutoLoginRef.current;
                doAutoLogin_1(userToken, lastPasswordUpdateDate, authToken, profileNo)
                    .then(function (tvingAccount) {
                    setIsLoaded(true);
                    var locationPath = locationPathRef.current;
                    if (entryData.redirectUrl) {
                        if (tvingAccount && (tvingAccount === null || tvingAccount === void 0 ? void 0 : tvingAccount.ticketTier) < TICKET_TIER.STANDARD) {
                            PageNavigator.replace('/welcome/login');
                        }
                        return;
                    }
                    if (tvingAccount && (tvingAccount === null || tvingAccount === void 0 ? void 0 : tvingAccount.ticketTier) < TICKET_TIER.STANDARD) {
                        PageNavigator.replace('/welcome/login');
                    }
                    else if (locationPath === '/' || !profileNo) {
                        PageNavigator.replace('/profile');
                    }
                })
                    .catch(function () {
                    setIsLoaded(true);
                    PageNavigator.replace('/onboarding');
                });
            }
            else {
                setIsLoaded(true);
                var locationPath = locationPathRef.current;
                if (locationPath !== '/login' &&
                    locationPath !== '/join' &&
                    locationPath !== '/login/qr' &&
                    locationPath !== '/login/tv' &&
                    locationPath !== '/login/tv/input') {
                    PageNavigator.replace('/onboarding');
                }
            }
        }
        else {
            setIsLoaded(true);
        }
    }, [profileNo, userToken, entryData, locationPathRef, doAutoLoginRef, authTokenRef, setIsLoaded, accessTokenRef, lastPasswordUpdateDate]);
    return null;
    // return <StyledEntryLayer isShow={isShow}>{isShow ? <SplashLayer isPlay={isPlay} setShow={setShow} /> : null}</StyledEntryLayer>;
};
export default EntryLayer;
